import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  EffectivePermission,
  PagedUsers,
  Permission,
  PermissionOverride,
  PermissionOverrideSet,
  Role,
  User,
  UserCreateInput,
  UserCreatedResponse,
  UserUpdateInput,
  UsersDashboardStats,
} from "./schemas";

const KEY = "usuarios";
const KEY_ROLES = "roles";
const KEY_PERMS = "permissions";

export type UsuariosFilters = {
  page?: number;
  size?: number;
  rol_id?: string | null;
  activo?: boolean | null;
  search?: string;
};

function buildParams(f: UsuariosFilters): Record<string, string | number> {
  const p: Record<string, string | number> = { page: f.page ?? 1, size: f.size ?? 50 };
  if (f.rol_id) p.rol_id = f.rol_id;
  if (f.activo !== null && f.activo !== undefined) p.activo = f.activo ? "true" : "false";
  if (f.search) p.search = f.search;
  return p;
}

export function useUsuarios(filters: UsuariosFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => apiCall<PagedUsers>("get", "/api/v1/users", { searchParams: buildParams(filters) }),
  });
}

export function useUsuariosStats() {
  return useQuery({
    queryKey: [KEY, "stats"],
    queryFn: () => apiCall<UsersDashboardStats>("get", "/api/v1/users/dashboard"),
  });
}

export function useUsuario(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<User>("get", `/api/v1/users/${id}`),
    enabled: !!id,
  });
}

export function useCrearUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UserCreateInput) =>
      apiCall<UserCreatedResponse>("post", "/api/v1/users", { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useEditarUsuario(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UserUpdateInput) =>
      apiCall<User>("patch", `/api/v1/users/${id}`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useBorrarUsuario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ message: string }>("delete", `/api/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useRegenerarPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, custom_password }: { id: string; custom_password?: string }) =>
      apiCall<UserCreatedResponse>("post", `/api/v1/users/${id}/regenerate-password`, {
        json: { custom_password: custom_password || null },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useReset2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<{ message: string }>("post", `/api/v1/users/${id}/reset-2fa`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

// ─── Roles & Permissions catálogo ───────────────────────────────────────
export function useRoles() {
  return useQuery({
    queryKey: [KEY_ROLES],
    queryFn: () => apiCall<Role[]>("get", "/api/v1/roles"),
    staleTime: 5 * 60 * 1000, // catálogo cambia rara vez
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: [KEY_PERMS],
    queryFn: () => apiCall<Permission[]>("get", "/api/v1/permissions"),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Efectivos + overrides por usuario ──────────────────────────────────
export function useEffectivePermissions(userId: string | null) {
  return useQuery({
    queryKey: [KEY, "permissions", userId],
    queryFn: () => apiCall<EffectivePermission[]>("get", `/api/v1/users/${userId}/permissions`),
    enabled: !!userId,
  });
}

export function useOverrides(userId: string | null) {
  return useQuery({
    queryKey: [KEY, "overrides", userId],
    queryFn: () => apiCall<PermissionOverride[]>("get", `/api/v1/users/${userId}/permission-overrides`),
    enabled: !!userId,
  });
}

export function useSetOverride(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codigo, granted, motivo }: { codigo: string } & PermissionOverrideSet) =>
      apiCall<PermissionOverride>("put", `/api/v1/users/${userId}/permission-overrides/${codigo}`, {
        json: { granted, motivo: motivo || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, "overrides", userId] });
      qc.invalidateQueries({ queryKey: [KEY, "permissions", userId] });
    },
  });
}

export function useClearOverride(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (codigo: string) =>
      apiCall<{ message: string }>("delete", `/api/v1/users/${userId}/permission-overrides/${codigo}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, "overrides", userId] });
      qc.invalidateQueries({ queryKey: [KEY, "permissions", userId] });
    },
  });
}
